import { React, Theme, variantProvider } from '@/app'
import { ActivityIndicator, Text, TextInput, View } from '@/components'
import { useForm, createForm, yup, useValidate } from '@codeleap/common'
import { TextInputProps } from '@codeleap/web'

const validator = yup.string().trim()
  .transform(value => (value || '').trim())
  .test(
    'optional-comment-length',
    'Comment must be between 5 and 256 characters',
    function (value) {
      if (!value || value.length === 0) return true
      return value.length >= 5 && value.length <= 256
    },
  )
  .verifyProfanity()

const commentForm = () => {
  return createForm('feed-comment', {
    text: {
      type: 'text',
      placeholder: `Add comment`,
      validate: validator,
    },
  })
}

const useCommentForm = (initialValue?: string) => {
  return useForm(commentForm, {
    initialState: {
      text: initialValue,
    },
    validateOn: 'change',
  })
}

type CommentInputProps = Partial<TextInputProps> & {
  onSend?: () => void
  form: ReturnType<typeof useCommentForm>
  loading?: boolean
  customError?: string
}

export const FeedCommentInput = ({ form, onSend, loading, customError }: CommentInputProps) => {

  const validation = useValidate(
    form.values.text,
    validator,
  )

  const errorMessage = React.useMemo(() => {
    let error = null
    if (customError) error = customError
    if (validation.message) error = validation.message

    if (!error) return null

    return (
      <View style={styles.errorWrapper}>
        {<Text text={error} variants={['p3']} style={styles.error} />}
      </View>
    )
  }, [validation.message, customError])

  const disabled = !form.isValid

  const rightIcon = React.useMemo(() => {
    if (loading) return <ActivityIndicator variants={['small']} />

    return {
      onPress: onSend,
      name: 'arrow-up',
      disabled,
    }
  }, [loading])

  return <TextInput
    debugName='Feed comment input'
    {...form.register('text')}
    label={null}
    multiline
    rightIcon={rightIcon}
    validate={null}
    variants={['noMinHeight', 'noError', 'floatingError']}
    onBlur={validation.onInputBlurred}
    onFocus={validation.onInputFocused}
    onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e?.key === 'Enter') {
        e.preventDefault()

        if (disabled) {
          return validation.onInputBlurred()
        } else {
          validation.onInputBlurred()
          return onSend?.()
        }
      }
    }}
    _error={errorMessage}
    styles={{
      'input:multiline': {
        maxHeight: Theme.values.itemHeight.default * 5,
      },
    }}
  />

}
const styles = variantProvider.createComponentStyle(theme => ({
  error: {
    backgroundColor: theme.colors.destructive2,
    color: theme.colors.neutral1,
    borderRadius: theme.borderRadius.small,
    ...theme.spacing.padding(0.5),
    textAlign: 'center',

  },
  errorWrapper: {
    position: 'absolute',
    bottom: `100%`,
    left: '50%',
    transform: 'translateX(-50%) translateY(-4px)',
  },
}), true)

FeedCommentInput.use = useCommentForm

